import { useRef } from 'react';
import { Workbook } from '@fortune-sheet/react';
import { BottomDrawer } from '../BottomDrawer/BottomDrawer';
import Button from '../_custom/Button/Button';
import LoadingSpinner from '../_custom/LoadingSpinner/LoadingSpinner';
import { useSheetEditorDrawerLogic } from './useSheetEditorDrawerLogic';
import { useColumnAutofit } from './useColumnAutofit';
import styles from './SheetEditorDrawer.module.scss';
import '@fortune-sheet/react/dist/index.css';

interface SheetEditorDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Presigned URL of the xlsx/csv file to edit. */
    fileUrl: string;
    /** Resolved MIME type — drives xlsx vs csv parsing. */
    mimeType?: string;
    /** Original file name; used as the drawer title and download base name. */
    fileName: string;
}

// Fortune-Sheet's default toolbar with the one-click currency button removed.
// Currency formatting is still reachable via the "format" dropdown; we drop the
// quick button per product (no multi-currency need). currency="₹" makes the
// Format dropdown default to the Indian Rupee instead of the library default (¥).
const TOOLBAR_ITEMS = [
    'undo',
    'redo',
    'format-painter',
    'clear-format',
    '|',
    'percentage-format',
    'number-decrease',
    'number-increase',
    'format',
    '|',
    'font',
    '|',
    'font-size',
    '|',
    'bold',
    'italic',
    'strike-through',
    'underline',
    '|',
    'font-color',
    'background',
    'border',
    'merge-cell',
    '|',
    'horizontal-align',
    'vertical-align',
    'text-wrap',
    'text-rotation',
    '|',
    'freeze',
    'conditionFormat',
    'filter',
    'link',
    'image',
    'comment',
    'quick-formula',
    'dataVerification',
    'splitColumn',
    'locationCondition',
    'screenshot',
    'search',
];

/**
 * Editable spreadsheet drawer. Opens an existing xlsx/csv in a Fortune-Sheet
 * grid (formulas + formula bar built in) inside the same BottomDrawer the
 * PlateEditor uses. Edits are local; "Download" exports back to xlsx/csv.
 */
const SheetEditorDrawer: React.FC<SheetEditorDrawerProps> = ({ open, onClose, fileUrl, mimeType, fileName }) => {
    const { sheets, loading, error, workbookRef, handleDownloadXlsx, handleDownloadCsv } = useSheetEditorDrawerLogic({
        open,
        fileUrl,
        mimeType,
        fileName,
    });

    const wrapperRef = useRef<HTMLDivElement>(null);
    const { fitColumnsToolbarItem } = useColumnAutofit({ workbookRef, wrapperRef, enabled: !loading && !error && !!sheets?.length });

    const headerRight =
        !loading && !error ? (
            <div className={styles.actions}>
                <Button type="button" text="Download .csv" variant="secondary" className={styles.headerBtn} onClick={handleDownloadCsv} />
                <Button type="button" text="Download .xlsx" variant="purple" className={styles.headerBtn} onClick={handleDownloadXlsx} />
            </div>
        ) : null;

    return (
        <BottomDrawer
            open={open}
            onClose={onClose}
            title={fileName}
            headerRight={headerRight}
            className={styles.drawer}
            bodyClassName={styles.body}
            height="100vh"
        >
            {loading && (
                <div className={styles.statusBox}>
                    <LoadingSpinner height="5rem" color="var(--primary-text-colour)" />
                </div>
            )}
            {error && (
                <div className={styles.statusBox}>
                    <p>Unable to open this file for editing.</p>
                </div>
            )}
            {!loading && !error && sheets && (
                <div className={styles.workbookWrapper} ref={wrapperRef}>
                    <Workbook
                        key={fileUrl}
                        ref={workbookRef}
                        data={sheets}
                        toolbarItems={TOOLBAR_ITEMS}
                        customToolbarItems={[fitColumnsToolbarItem]}
                        currency="₹"
                        showToolbar
                        showFormulaBar
                        showSheetTabs
                        allowEdit
                    />
                </div>
            )}
        </BottomDrawer>
    );
};

export default SheetEditorDrawer;
